import { blockRegistry } from './block-registry';
import { findBlockById } from './block-tree-utils';
import { isSectionTemplate } from './section-templates';
import type { EmailBlock } from '@/types/email-blocks';
import type { DragData } from '@/components/email-builder/DndProvider';

/**
 * Validation utilities for drag and drop operations
 */

export interface DropLocation {
  parentId: string | null;
  index: number;
}

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
}

/**
 * Validates if a block can be dropped at a specific location
 */
export function canAcceptDrop(
  dragData: DragData,
  dropLocation: DropLocation,
  blockTree: EmailBlock[]
): ValidationResult {
  // Determine the block type being dragged
  let blockType: string;
  let draggedBlockId: string | null = null;

  if (dragData.type === 'new-block' && dragData.blockType) {
    blockType = dragData.blockType;

    // If it's a section template, treat it as a section for validation
    if (isSectionTemplate(blockType)) {
      blockType = 'section';
    }
  } else if (dragData.type === 'existing-block' && dragData.blockId) {
    draggedBlockId = dragData.blockId;
    const block = findBlockById(blockTree, dragData.blockId);
    if (!block) {
      return { isValid: false, reason: 'Block not found' };
    }
    blockType = block.blockType;
  } else if (dragData.type === 'custom-block') {
    return { isValid: false, reason: 'Custom blocks not supported' };
  } else {
    return { isValid: false, reason: 'Invalid drag data' };
  }

  // Get block configuration
  const blockConfig = blockRegistry.get(blockType);
  if (!blockConfig) {
    return { isValid: false, reason: `Unknown block type: ${blockType}` };
  }

  // Validate parent-child relationship
  const parentBlock = dropLocation.parentId
    ? findBlockById(blockTree, dropLocation.parentId)
    : null;
  const parentType = parentBlock?.blockType || null;

  if (!blockRegistry.canAddChild(blockType, parentType)) {
    const parentName = parentType
      ? blockRegistry.get(parentType)?.displayName || parentType
      : 'canvas';
    return {
      isValid: false,
      reason: `Cannot place ${blockConfig.displayName} in ${parentName}`,
    };
  }

  // Check if parent can accept more children
  if (parentBlock) {
    const currentChildCount = parentBlock.childBlocks?.length || 0;
    // If moving within same parent, don't count the block being moved
    const adjustedCount =
      draggedBlockId && parentBlock.id === dragData.parentId
        ? currentChildCount - 1
        : currentChildCount;

    if (!blockRegistry.canAcceptMoreChildren(parentBlock.blockType, adjustedCount)) {
      return {
        isValid: false,
        reason: `${blockRegistry.get(parentBlock.blockType)?.displayName || 'Container'} is full`,
      };
    }
  }

  // Prevent dropping a block into itself or one of its own descendants
  // (would create a circular reference). The target parent must not be the
  // dragged block, nor live anywhere inside the dragged block's subtree.
  if (draggedBlockId && parentBlock) {
    const draggedBlock = findBlockById(blockTree, draggedBlockId);
    if (draggedBlock && isDescendantOf(draggedBlock, parentBlock.id, blockTree)) {
      return {
        isValid: false,
        reason: 'Cannot move a block into its own child',
      };
    }
  }

  return { isValid: true };
}

/**
 * Checks if a block is a descendant of a potential parent
 */
function isDescendantOf(
  block: EmailBlock,
  potentialParentId: string,
  blockTree: EmailBlock[]
): boolean {
  if (block.id === potentialParentId) {
    return true;
  }

  if (block.childBlocks && block.childBlocks.length > 0) {
    return block.childBlocks.some((child) =>
      isDescendantOf(child, potentialParentId, blockTree)
    );
  }

  return false;
}
